import {create} from 'zustand';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import type {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import type {SessionFilters, SessionTrack, WorkSession} from '../models/session.model';
import {MarketerTrackingFactory} from './marketerTrackingFactory';

interface MarketerTrackingState {
  // Selection
  selectedMarketerId: number | null;
  selectedDate: string | null; // YYYY-MM-DD

  // Selected day view
  selectedSession: WorkSession | null;
  selectedTrack: SessionTrack | null;
  loadingSelected: boolean;
  selectedError: string | null;

  // History pane (paginated list for the selected marketer)
  history: WorkSession[];
  historyPagination: Pagination | undefined;
  loadingHistory: boolean;
  historyError: string | null;

  // Actions
  setSelectedMarketer: (marketerId: number | null) => void;
  setSelectedDate: (date: string | null) => void;
  loadSessionForDate: (marketerId: number, date: string) => Promise<void>;
  loadMarketerHistory: (
    marketerId: number,
    page?: number,
    limit?: number
  ) => Promise<void>;
  clear: () => void;
}

export const useMarketerTrackingStore = create<MarketerTrackingState>((set, get) => {
  const getSessionsList = MarketerTrackingFactory.createGetSessionsListUseCase();
  const getSessionTrack = MarketerTrackingFactory.createGetSessionTrackUseCase();
  const getMarketerSessions = MarketerTrackingFactory.createGetMarketerSessionsUseCase();
  const errorHandler = StandardizedErrorHandler.getInstance();

  // Per-loader request tokens. Each call to a loader bumps the counter and
  // captures the value; on response, we drop the result if the counter has
  // moved on (= a newer request was issued for a different marketer/date).
  // Without this, switching marketers fast can leave the slow first
  // response overwriting the fast second response — i.e. the user sees
  // the previous marketer's data on the new marketer's row.
  let sessionLoadToken = 0;
  let historyLoadToken = 0;

  return {
    selectedMarketerId: null,
    selectedDate: null,

    selectedSession: null,
    selectedTrack: null,
    loadingSelected: false,
    selectedError: null,

    history: [],
    historyPagination: undefined,
    loadingHistory: false,
    historyError: null,

    setSelectedMarketer: (marketerId) => {
      // Wipe everything that's marketer-specific BEFORE kicking off new loads,
      // so the UI never briefly shows the previous marketer's session/history.
      set({
        selectedMarketerId: marketerId,
        selectedSession: null,
        selectedTrack: null,
        selectedError: null,
        history: [],
        historyPagination: undefined,
        historyError: null
      });

      if (marketerId === null) {return;}

      const date = get().selectedDate;
      if (date) {
        void get().loadSessionForDate(marketerId, date);
      }
      void get().loadMarketerHistory(marketerId);
    },

    setSelectedDate: (date) => {
      set({
        selectedDate: date,
        selectedSession: null,
        selectedTrack: null,
        selectedError: null
      });
      const marketerId = get().selectedMarketerId;
      if (marketerId !== null && date !== null) {
        void get().loadSessionForDate(marketerId, date);
      }
    },

    loadSessionForDate: async (marketerId, date) => {
      const token = ++sessionLoadToken;
      set({loadingSelected: true, selectedError: null});
      try {
        // Find the session for the given day (filtering by marketer + date).
        const filters: SessionFilters = {
          marketerId,
          from: date,
          to: date,
          page: 1,
          limit: 1
        };
        const {sessions} = await getSessionsList.execute(filters);

        // Race guard: bail if another request superseded us OR the user
        // navigated away from this (marketer, date) pair while we waited.
        if (token !== sessionLoadToken) {return;}
        const current = get();
        if (current.selectedMarketerId !== marketerId || current.selectedDate !== date) {
          return;
        }

        if (sessions.length === 0) {
          set({selectedSession: null, selectedTrack: null, loadingSelected: false});
          return;
        }
        const session = sessions[0];
        const track = await getSessionTrack.execute(session.id);

        // Re-check after the second await — selection may have changed
        // during the track fetch too.
        if (token !== sessionLoadToken) {return;}
        const stillCurrent = get();
        if (
          stillCurrent.selectedMarketerId !== marketerId ||
          stillCurrent.selectedDate !== date
        ) {
          return;
        }

        set({selectedSession: session, selectedTrack: track, loadingSelected: false});
      } catch (err) {
        if (token !== sessionLoadToken) {return;}
        const appError = errorHandler.toAppError(err);
        set({selectedError: appError.message, loadingSelected: false});
        errorHandler.handleError(err);
      }
    },

    loadMarketerHistory: async (
      marketerId,
      page = 1,
      limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE
    ) => {
      const token = ++historyLoadToken;
      set({loadingHistory: true, historyError: null});
      try {
        const result = await getMarketerSessions.execute(marketerId, {page, limit});

        // Race guard: stale response → drop. Also confirm the marketer
        // hasn't changed since we kicked off.
        if (token !== historyLoadToken) {return;}
        if (get().selectedMarketerId !== marketerId) {return;}

        set({
          history: result.sessions,
          historyPagination: result.pagination,
          loadingHistory: false
        });
      } catch (err) {
        if (token !== historyLoadToken) {return;}
        const appError = errorHandler.toAppError(err);
        set({historyError: appError.message, loadingHistory: false});
        errorHandler.handleError(err);
      }
    },

    clear: () => {
      // Bump tokens so any inflight requests drop their results on return.
      sessionLoadToken++;
      historyLoadToken++;
      set({
        selectedMarketerId: null,
        selectedDate: null,
        selectedSession: null,
        selectedTrack: null,
        loadingSelected: false,
        selectedError: null,
        history: [],
        historyPagination: undefined,
        loadingHistory: false,
        historyError: null
      });
    }
  };
});
